import { createContext, useContext, useState, useEffect } from 'react';
import { 
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  updateProfile
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { auth, db, ADMIN_EMAIL, ADMIN_UID } from '../config/firebase';
import { COLLECTIONS } from '../utils/constants';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check if user is admin - ONLY specific email/UID allowed
  const checkAdminStatus = async (user) => {
    if (!user) return false;

    // STRICT CHECK: Only allow specific admin email OR UID
    if (user.email === ADMIN_EMAIL || user.uid === ADMIN_UID) {
      return true;
    }

    // No other checks - only the hardcoded admin email/UID is allowed
    return false;
  };

  // Load user profile from Firestore
  const loadUserProfile = async (user) => {
    if (!user) return null;

    // Skip loading profile for admin user
    if (user.email === ADMIN_EMAIL || user.uid === ADMIN_UID) {
      return null;
    }

    try {
      const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, user.uid));
      if (userDoc.exists()) {
        return { id: userDoc.id, ...userDoc.data() };
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }

    return null;
  };

  // Sign in with email and password
  const signIn = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return { success: true, user: result.user };
    } catch (error) {
      return { success: false, error: error.code };
    }
  };

  // Sign up with email and password
  const signUp = async (email, password, userData) => {
    try {
      // Prevent admin email from being registered as regular user
      if (email === ADMIN_EMAIL) {
        return { success: false, error: 'This email is reserved for admin access only.' };
      }

      const result = await createUserWithEmailAndPassword(auth, email, password);
      const user = result.user;
      console.log('[AuthContext] User created in Firebase Auth:', user.uid);

      // Update profile
      await updateProfile(user, {
        displayName: userData.name,
        photoURL: userData.profileImage || null
      });

      // Create user document in Firestore (NOT for admin)
      const userDoc = {
        name: userData.name,
        email,
        phone: userData.phone,
        country: userData.country || '',
        state: userData.state || '',
        city: userData.city || '',
        instagram: userData.instagram || '',
        instagramName: userData.instagramName || '',
        facebook: userData.facebook || '',
        facebookName: userData.facebookName || '',
        youtube: userData.youtube || '',
        youtubeName: userData.youtubeName || '',
        upiName: userData.upiName || '',
        upiNumber: userData.upiNumber || '',
        profileImage: userData.profileImage || null,
        vipTokens: [],
        totalTokens: 0,
        createdAt: serverTimestamp(),
        status: 'active',
        approvalStatus: 'pending',
        approvedAt: null,
        approvedBy: null
      };
      
      console.log('[AuthContext] Creating Firestore document for user:', user.uid, userDoc);
      await setDoc(doc(db, COLLECTIONS.USERS, user.uid), userDoc);
      console.log('[AuthContext] Firestore document created successfully');

      return { success: true, user };
    } catch (error) {
      return { success: false, error: error.code };
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.code };
    }
  };

  // Update user profile
  const updateUserProfile = async (updates) => {
    if (!currentUser) return { success: false, error: 'No user logged in' };

    try {
      const userRef = doc(db, COLLECTIONS.USERS, currentUser.uid);
      await setDoc(userRef, updates, { merge: true });

      // Reload profile
      const profile = await loadUserProfile(currentUser);
      setUserProfile(profile);

      return { success: true };
    } catch (error) {
      return { success: false, error: error.code };
    }
  };

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        // Check admin status
        const adminStatus = await checkAdminStatus(user);
        setIsAdmin(adminStatus);

        // Skip profile listener for admin
        if (user.email === ADMIN_EMAIL || user.uid === ADMIN_UID) {
          setUserProfile(null);
          setLoading(false);
          return;
        }

        // Set up real-time listener for user profile
        const userDocRef = doc(db, COLLECTIONS.USERS, user.uid);
        const unsubscribeProfile = onSnapshot(
          userDocRef,
          (docSnapshot) => {
            if (docSnapshot.exists()) {
              const profile = { id: docSnapshot.id, ...docSnapshot.data() };
              console.log('[AuthContext] User profile updated:', profile.approvalStatus);
              setUserProfile(profile);
            } else {
              setUserProfile(null);
            }
            setLoading(false);
          },
          (error) => {
            console.error('Error loading user profile:', error);
            setUserProfile(null);
            setLoading(false);
          }
        );

        // Return cleanup function for profile listener
        return () => unsubscribeProfile();
      } else {
        setUserProfile(null);
        setIsAdmin(false);
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    isAdmin,
    loading,
    signIn,
    signUp,
    signOut,
    updateUserProfile,
    checkAdminStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
